/**
 * As linhas do que JÁ VEM no pacote, nascendo do catálogo em vez da memória do
 * vendedor.
 *
 * Até aqui, quem punha um HUB-START-VOZ no carrinho precisava LEMBRAR de
 * adicionar à mão uma segunda linha com 10 HUB-RAMAL a R$ 0,00. Esquecer não
 * dava erro em lugar nenhum: a proposta saía anunciando a plataforma e calada
 * sobre os dez ramais que o cliente está levando. Agora o produto declara o que
 * embute (`products.includes`, backend PR #220) e a linha é derivada.
 *
 * REGRAS QUE VÊM DO CONTRATO, e o motivo de cada uma:
 *
 * 1. A linha derivada vale ZERO, não o preço do membro. O preço já está no
 *    pacote — linha com valor cheio dobraria o MRR da proposta.
 * 2. Pacote e avulso do mesmo código NÃO se fundem. A PRO20260086 tem
 *    1 × HUB-NUMERO a R$ 0 (incluso) e 6 × HUB-NUMERO a R$ 25 (avulso): o
 *    documento soma 1+6 na capa e o total não infla. Fundir perderia a conta.
 * 3. Membro INATIVO continua gerando linha. HUB-RAMAL e HUB-COMBO estão na fila
 *    de inativação e são justamente os membros dos dois pacotes novos; esconder
 *    a linha por status faria a capa omitir o que o cliente recebe.
 * 4. Nome e descrição da linha derivada são do VENDEDOR. A quantidade, o preço
 *    e o produto são derivados e ficam travados; o texto não, porque é ele que
 *    diz "inclusos no pacote" e evita duas linhas idênticas com preços
 *    diferentes lado a lado no documento.
 * 5. Linha zerada ANTIGA, sem marca de origem, é ADOTADA em vez de duplicada.
 *    As propostas já feitas na convenção manual não podem ganhar uma segunda
 *    linha do mesmo membro só porque o campo passou a existir.
 * 6. Nunca apagamos linha que não seja nossa. Some a derivada quando o pacote
 *    sai do carrinho; linha sem marca fica, mesmo zerada — apagar o trabalho do
 *    vendedor seria pior que deixar uma linha a mais que ele vê e resolve.
 *
 * Um nível só: pacote que fosse membro de outro pacote não é resolvido em
 * cascata. Não existe no catálogo hoje e resolver em cascata sem caso real
 * seria inventar regra.
 */

export interface ItemDaProposta {
  id: any;
  productId: number | null;
  productCode: string;
  name: string;
  description: string;
  type: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  subtotal: number;
  /** Id do PRODUTO-pacote que gerou esta linha. Ausente = linha do vendedor. */
  includedByProductId?: number | null;
  [k: string]: any;
}

export interface MembroDoPacote {
  productId: number;
  /** Quanto vem em UMA unidade do pacote. */
  quantity: number;
  code?: string;
  name?: string;
  type?: string;
  description?: string;
}

export const ehLinhaDerivada = (item: any): boolean =>
  Number(item?.includedByProductId) > 0;

/**
 * Os membros de um pacote. Prefere o `includedProducts` que o GET /products já
 * resolve (vem inclusive com membro inativo); cai no `includes` cru resolvido
 * contra o catálogo quando a resposta é antiga.
 */
export function membrosDoPacote(produto: any, catalogo: Map<number, any>): MembroDoPacote[] {
  const crus: any[] = Array.isArray(produto?.includedProducts) && produto.includedProducts.length
    ? produto.includedProducts
    : Array.isArray(produto?.includes)
      ? produto.includes
      : [];
  const membros: MembroDoPacote[] = [];
  for (const c of crus) {
    const productId = Number(c?.productId);
    const quantity = Number(c?.quantity);
    if (!(productId > 0) || !(quantity > 0)) continue;
    const doCatalogo = catalogo.get(productId);
    membros.push({
      productId,
      quantity,
      code: c?.code ?? doCatalogo?.code ?? '',
      name: c?.name ?? doCatalogo?.name ?? '',
      type: doCatalogo?.type ?? 'MRR',
      description: doCatalogo?.description ?? '',
    });
  }
  return membros;
}

const mesmaLinha = (a: ItemDaProposta, b: ItemDaProposta): boolean =>
  a.productId === b.productId &&
  a.productCode === b.productCode &&
  a.type === b.type &&
  a.quantity === b.quantity &&
  a.unitPrice === b.unitPrice &&
  a.discount === b.discount &&
  a.subtotal === b.subtotal &&
  a.includedByProductId === b.includedByProductId &&
  a.name === b.name &&
  a.description === b.description;

let sequencia = 0;
const novoIdPadrao = () => Date.now() + (sequencia = (sequencia + 1) % 100000);

/**
 * Devolve a lista com as linhas derivadas em dia. **Devolve a MESMA referência
 * quando nada muda** — é o que deixa o efeito que chama isto a cada render
 * convergir em vez de rodar para sempre.
 */
export function sincronizarInclusos(
  itens: ItemDaProposta[],
  produtos: any[],
  novoId: () => any = novoIdPadrao,
): ItemDaProposta[] {
  if (!Array.isArray(itens) || !itens.length) return itens;
  const catalogo = new Map<number, any>(
    (Array.isArray(produtos) ? produtos : []).map((p: any) => [Number(p?.id), p]),
  );

  const doVendedor = itens.filter(i => !ehLinhaDerivada(i));
  const derivadasAtuais = itens.filter(ehLinhaDerivada);

  // O que o carrinho DEVERIA ter, somando por par (pacote, membro): duas linhas
  // do mesmo pacote valem uma linha derivada com a soma, porque o documento
  // conta quantidade por código.
  const esperado = new Map<string, { pacoteId: number; membro: MembroDoPacote; quantidade: number }>();
  for (const item of doVendedor) {
    const pacoteId = Number(item?.productId);
    const pacote = catalogo.get(pacoteId);
    if (!pacote) continue;
    const qtdPacote = Number(item?.quantity) || 0;
    if (qtdPacote <= 0) continue;
    for (const membro of membrosDoPacote(pacote, catalogo)) {
      const chave = `${pacoteId}:${membro.productId}`;
      const ja = esperado.get(chave);
      if (ja) ja.quantidade += membro.quantity * qtdPacote;
      else esperado.set(chave, { pacoteId, membro, quantidade: membro.quantity * qtdPacote });
    }
  }

  const derivadaPorChave = new Map<string, ItemDaProposta>();
  for (const d of derivadasAtuais) {
    derivadaPorChave.set(`${Number(d.includedByProductId)}:${Number(d.productId)}`, d);
  }

  const adotadas = new Set<ItemDaProposta>();
  const novasPorChave = new Map<string, ItemDaProposta>();

  for (const [chave, alvo] of Array.from(esperado.entries())) {
    const anterior =
      derivadaPorChave.get(chave) ||
      // Convenção manual: linha zerada do mesmo membro, ainda sem marca.
      doVendedor.find(
        i =>
          !adotadas.has(i) &&
          Number(i.productId) === alvo.membro.productId &&
          Number(i.unitPrice) === 0 &&
          Number(i.subtotal) === 0,
      );
    if (anterior && !derivadaPorChave.has(chave)) adotadas.add(anterior);

    const linha: ItemDaProposta = {
      ...(anterior || {}),
      id: anterior?.id ?? novoId(),
      productId: alvo.membro.productId,
      productCode: alvo.membro.code || '',
      // O texto é do vendedor: só escrevemos quando a linha nasce agora.
      name: anterior?.name || `${alvo.membro.name || ''} (incluso no pacote)`.trim(),
      description: anterior?.description ?? (alvo.membro.description || ''),
      type: alvo.membro.type || 'MRR',
      quantity: alvo.quantidade,
      unitPrice: 0,
      discount: 0,
      subtotal: 0,
      includedByProductId: alvo.pacoteId,
    };
    novasPorChave.set(chave, anterior && mesmaLinha(anterior, linha) ? anterior : linha);
  }

  // Remonta na ordem: cada derivada logo depois da ÚLTIMA linha do seu pacote,
  // que é a ordem em que o documento lê e a que o vendedor desenhou arrastando.
  const ultimaPosicaoDoPacote = new Map<number, number>();
  const sobreviventes = doVendedor.filter(i => !adotadas.has(i));
  sobreviventes.forEach((item, idx) => {
    const pid = Number(item?.productId);
    if (pid > 0) ultimaPosicaoDoPacote.set(pid, idx);
  });

  const porPosicao = new Map<number, ItemDaProposta[]>();
  const semAncora: ItemDaProposta[] = [];
  for (const [chave, linha] of Array.from(novasPorChave.entries())) {
    const pacoteId = Number(chave.split(':')[0]);
    const pos = ultimaPosicaoDoPacote.get(pacoteId);
    if (pos === undefined) { semAncora.push(linha); continue; }
    const lista = porPosicao.get(pos);
    if (lista) lista.push(linha);
    else porPosicao.set(pos, [linha]);
  }

  const saida: ItemDaProposta[] = [];
  sobreviventes.forEach((item, idx) => {
    saida.push(item);
    const derivadas = porPosicao.get(idx);
    if (derivadas) saida.push(...derivadas);
  });
  saida.push(...semAncora);

  const igual =
    saida.length === itens.length && saida.every((linha, i) => linha === itens[i]);
  return igual ? itens : saida;
}
